import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
/*
 * ACOPLAMENTO ENTRE DOMINIOS, declarado.
 *
 * Esta e uma tela de OCPP e esta importando do modulo de FROTA. O que se
 * importa e `valorDeParcela`, que nao sabe nada de onibus: ela decide entre
 * os dois zeros ("zero por ausencia de topologia" e "zero por ausencia de
 * problema"), e a distincao vale para carregador igual vale para onibus.
 *
 * Fica REGISTRADO em vez de implicito: a funcao nasceu em `components/fleet`
 * porque foi la que o defeito apareceu, e o lugar certo dela e um modulo
 * comum (`lib/`) no dia em que houver o terceiro consumidor. Backlog
 * nomeado, nao ressalva circulando.
 */
import { maiuscula, valorDeParcela } from "../components/fleet/aggregates.js";
import { ESTILO_DEGRADADO_TEXTO } from "../components/fleet/FleetCells.js";
import { fmtAge } from "../components/substation/bands.js";
/*
 * O CONTRATO VEM DE UM LUGAR SÓ — e aqui havia a primeira das CINCO cópias.
 *
 * A declaração local desta tela tinha `location`, `nominalPowerKw` e
 * `connectors`, e NÃO tinha `label`, `serialNumber` nem `garageId`. Ela também
 * tinha `lastHeartbeatAt`, campo que o servidor REMOVEU do contrato — e o
 * `tsc --noEmit` passava limpo nos quatro pacotes, porque `r.json()` é `any` e
 * anotar um `any` é afirmar, não verificar. Medido ao vivo antes do conserto:
 * os 19 cards imprimiam "Último heartbeat: —", porque `undefined` atravessava
 * o formatador e saía travessão.
 *
 * `LinhaDeCarregador` e os envelopes são de `@nxvolt/shared`, publicados UMA
 * vez. `chargers-contrato.ts` é a camada de apresentação deles — o que o `tsc`
 * não vê (idade assinada, `means`, `sharedBy`, `labelIsIdentifying`) mora lá,
 * com teste.
 */
import {
  contatoApresentado,
  configuracaoDeCarregadores,
  erroDeFonteNaoSuportada,
  fabricanteEModelo,
  fonteForneceCapacidade,
  fraseDeFalhaDeComando,
  idadeCurtaDoContato,
  legendaDeConectoresDegradados,
  motivoDeComandoIndisponivel,
  nomeDoCarregador,
  ressalvaDeRecorteDeCarregadores,
  semSinalDe,
  type LinhaDeCarregador,
  type RespostaDeCarregadores,
} from "./chargers-contrato.js";
/* O vocabulário de falha de carga — frase em português, sem o token, com a de
   403 que NÃO manda tentar de novo. Ver `lib/falha-de-carga.ts`. */
import { falhaDe, fraseDeFalhaDeCarga, type FalhaDeCarga } from "../lib/falha-de-carga.js";
import { useAuth } from "../lib/auth-context.js";
import { Skeleton } from "../components/Skeleton.js";
import { DownloadButton } from "../components/DownloadButton.js";

/* Os nomes locais ficam como ALIAS do tipo publicado — o JSX abaixo continua
   lendo `Charger`, e não há uma segunda definição para divergir. */
type Charger = LinhaDeCarregador;
type Connector = LinhaDeCarregador["connectors"][number];

/**
 * O RÓTULO DE ESTADO DO CONECTOR — e ele SUSTENTA o argumento de 1.4.11.
 *
 * Fica EXPORTADO e fora do componente porque a premissa "existe rótulo textual"
 * precisa ser VERIFICÁVEL. Enquanto ela vivia numa expressão inline no JSX, era
 * afirmada e não provada — e foi assim que `status: "   "` atravessou: string de
 * espaços é truthy, `||` devolve os espaços, e o chip renderizava só o marcador
 * redondo colorido. Ver `ChargersPage.test.ts`.
 */
export function rotuloDoStatusDoConector(status: string | null | undefined): string {
  /*
    `?.trim() ||` e NAO `||` — e a diferenca e o estado que o conserto anterior
    se propos a tornar impossivel e nao tornou.

    STRING DE ESPACOS E TRUTHY. Com `status: "   "` o `||` devolvia os espacos e
    o chip renderizava so o marcador redondo colorido. Medido na tela e em jsdom:
    `""`, `null` e ausente davam "Desconhecido"; `"   "` e `"\t\n"` davam
    `#1` e nada.

    `trim()` tambem alcanca o branco INVISIVEL que `\s` cobre — ` ` e
    `\u2009` — e nesses o defeito e pior, porque nem aparece na inspecao do
    payload.

    E o aparo vale para status LEGITIMO tambem: o chip e `inline-flex` com
    `gap`, entao `" Charging "` empurra o marcador e desalinha a linha inteira
    de conectores.
  */
  return status?.trim() || "Desconhecido";
}

/**
 * Semântica visual do status OCPP em 4 cores do design system.
 *
 * ---------------------------------------------------------------------------
 * TINTA SEMÂNTICA, e não paleta crua — o mesmo número que o botão primário.
 *
 * `--nx-orange-500` era a tinta do pill "Carregando", a 10,8 px: **3,08:1 no
 * tema claro**, exatamente o valor que fez o `.nx-btn` ser corrigido, intacto um
 * componente ao lado — e aqui reprovando SEM degradação nenhuma por cima.
 *
 * Os `-500` são cor de PREENCHIMENTO e não viram com o tema; os tokens
 * semânticos (`--nx-orange-ink`, `--nx-band-*-ink`, `--nx-alert-ink`) resolvem o
 * par POR TEMA e têm as razões medidas em `styles/contrast.test.ts`. Medido:
 * `--nx-success-500` dava 3,30:1 no claro e `--nx-danger` 3,95:1 no escuro — os
 * três pills reprovavam, não só o laranja.
 *
 * O FUNDO TAMBÉM VIROU SÓLIDO — e sem ele a primeira correção ficava pela metade.
 *
 * A rodada anterior trocou só a TINTA e mediu contra BRANCO PURO, ignorando o
 * `color-mix(... 12%, transparent)` que este mesmo arquivo escreve três linhas
 * abaixo. Com fundo translúcido a razão real depende do que está atrás — que é
 * o que o comentário anterior DIZIA, enquanto o relatório reportava a medição
 * como se não houvesse fundo. Medido sobre a composição real, três dos quatro
 * pills reprovavam AA no claro — DOIS depois da "correção".
 *
 * Agora o PAR inteiro é sólido e a razão deixa de depender da dobra. Três dos
 * quatro pares já existiam na frota e já são medidos por `contrast.test.ts`
 * (`--nx-state-live-*`, `--nx-alert-*`, `--nx-state-unknown-*`); só o laranja
 * precisou de fundo próprio (`--nx-orange-bg`), que entrou medido.
 */
type ChargerState = "online" | "charging" | "faulted" | "offline";

interface StateStyle {
  label: string;
  color: string;         // var CSS pra texto/borda/dot
  bg: string;            // fundo SOLIDO do par (ver o cabecalho)
  dotAnim: boolean;      // se pulsa (usado em charging + faulted)
}
function styleFor(state: ChargerState): StateStyle {
  switch (state) {
    case "charging":
      return {
        label: "Carregando",
        color: "var(--nx-orange-ink)",
        bg: "var(--nx-orange-bg)",
        dotAnim: true,
      };
    case "online":
      return {
        label: "livre",
        color: "var(--nx-state-live-ink)",
        bg: "var(--nx-state-live-bg)",
        dotAnim: false,
      };
    case "faulted":
      return {
        label: "Falha",
        color: "var(--nx-alert-ink)",
        bg: "var(--nx-alert-bg)",
        dotAnim: true,
      };
    case "offline":
      return {
        label: "Offline",
        color: "var(--nx-state-unknown-ink)",
        bg: "var(--nx-state-unknown-bg)",
        dotAnim: false,
      };
  }
}

/** Deriva estado do CP a partir do link WS + StatusNotification[0]. */
function deriveStationState(c: Charger): ChargerState {
  if (!c.connected) return "offline";
  const station = c.connectors.find((cn) => cn.connectorId === 0);
  if (!station) {
    // Fallback: usa status agregado dos conectores físicos
    if (c.connectors.some((cn) => cn.status === "Faulted" || cn.status === "Unavailable")) return "faulted";
    if (c.connectors.some((cn) => ["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(cn.status))) return "charging";
    return "online";
  }
  if (station.status === "Faulted" || station.status === "Unavailable") return "faulted";
  if (["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(station.status)) return "charging";
  return "online";
}

/** Estado do conector físico. */
function deriveConnectorState(status: string): ChargerState {
  if (status === "Available") return "online";
  if (["Charging","Preparing","SuspendedEV","SuspendedEVSE","Finishing"].includes(status)) return "charging";
  if (["Faulted","Unavailable"].includes(status)) return "faulted";
  return "offline";
}

/*
  `statusBadge` SAIU — era código morto, e código morto mente para quem lê.

  Esta tela desenha o estado do conector com `styleFor` + `rotuloDoStatusDoConector`
  (o par sólido medido em `contrast.test.ts`), e `statusBadge` não era chamado em
  lugar nenhum. Deixá-lo sugeria um segundo caminho de renderização de estado —
  e um segundo caminho é por onde nasce a segunda verdade sobre o mesmo dado,
  que é a família que esta frente inteira persegue.

  A versão viva da função continua em `ChargerDetailPage`, que a USA.
*/

/**
 * Ícone SVG do carregador rápido (poste EV com plug). Nas cores do portal —
 * a cor da carroceria segue a variável passada em `accent` pra combinar com
 * o estado (verde/laranja/vermelho/cinza). Zero hex hardcoded.
 */
function ChargerIcon({ accent }: { accent: string }) {
  return (
    <svg
      width="52"
      height="72"
      viewBox="0 0 52 72"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Ícone do carregador"
    >
      <defs>
        <filter id="cp-shadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" floodOpacity="0.25" />
        </filter>
      </defs>
      <g filter="url(#cp-shadow)">
        {/* base */}
        <rect x="6" y="60" width="40" height="8" rx="2" fill="var(--nx-neutral-300)" />
        {/* corpo */}
        <rect
          x="10"
          y="10"
          width="32"
          height="52"
          rx="6"
          fill={accent}
          stroke="var(--nx-bg)"
          strokeWidth="1.5"
        />
        {/* tela */}
        <rect x="14" y="16" width="24" height="18" rx="2" fill="var(--nx-navy-900)" opacity=".85" />
        <line x1="17" y1="21" x2="35" y2="21" stroke="var(--nx-success-500)" strokeWidth="1.5" strokeLinecap="round" />
        <line x1="17" y1="25" x2="30" y2="25" stroke="var(--nx-success-500)" strokeWidth="1.2" strokeLinecap="round" opacity=".8" />
        <line x1="17" y1="29" x2="27" y2="29" stroke="var(--nx-success-500)" strokeWidth="1.2" strokeLinecap="round" opacity=".6" />
        {/* raio */}
        <path
          d="M27 40 L22 50 L26 50 L24 58 L31 46 L27 46 Z"
          fill="var(--nx-bg)"
          opacity=".95"
        />
      </g>
    </svg>
  );
}

/*
  `fmt(iso)` SAIU DAQUI, e a remoção é parte do conserto.

  Ele só existia para imprimir `lastHeartbeatAt`, e era ele que engolia o
  defeito: com o campo removido do contrato, `undefined` entrava e saía
  travessão — a tela dizia "Último heartbeat: —" nos 19 cards sem nada quebrar.
  A idade agora vem de `contatoApresentado`, que recebe o valor ASSINADO e tem
  `fmtAge` como único formatador (o mesmo da frota e da subestação, cujo
  cabeçalho registra por que não há clamp lá dentro).
*/

export function ChargersPage() {
  const { user } = useAuth();
  /*
    O ENVELOPE INTEIRO FICA NO ESTADO, e não só `chargers`.

    `/api/chargers` deixou de ser `{ chargers }` e virou envelope com
    `configuration.staleAfterSeconds` (o limiar de frescor, que é do SERVIDOR),
    `sources` (quem não fornece o quê) e `totalVisivel` × `amostrados` (o
    recorte, para ele ser DECLARÁVEL). Guardar só o array jogava fora as três
    coisas e obrigaria a tela a inventar cada uma.

    `null` = "ainda não carregou" (renderiza skeleton). Sem essa distinção o
    estado vazio pisca antes do primeiro fetch.
  */
  const [envelope, setEnvelope] = useState<RespostaDeCarregadores | null>(null);
  /*
    A FALHA DE CARGA É `FalhaDeCarga`, NÃO uma string — e a razão é a vitrine.

    Era `string | null`, alimentada por `.catch((e) => setError(e.message))`
    sobre `new Error(String(r.status))`: a tela escrevia **"Erro ao carregar
    carregadores: 403"**, um inteiro nu, pior que o `Error: HTTP 403` que a
    frota já tinha. Esta é a tela que a Noxxon vê na demo.
  */
  const [error, setError] = useState<FalhaDeCarga | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /* A causa da última falha de comando, POR CARREGADOR — ver `aplicarResposta`. */
  const [falha, setFalha] = useState<{ cpId: string; frase: string } | null>(null);
  const [statusFilter, setStatusFilter] = useState<"all" | ChargerState>("all");
  const [locationFilter, setLocationFilter] = useState("");

  const load = useCallback(() => {
    fetch("/api/chargers", { credentials: "include" })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)),
      )
      .then((d: RespostaDeCarregadores) => {
        setEnvelope(d);
        /*
          O SUCESSO LIMPA O ERRO — e a falta disto era um ERRO PEGAJOSO.

          Sem este `setError(null)`, um 403 de um tick ficava na tela para
          sempre: medido, rede restaurada e 2,5 ciclos do `setInterval` depois,
          `aindaComErro: true, cards: 0`. Um blip de rede de um segundo
          congelava a vitrine até recarregar o navegador. `modules-extra`
          sempre teve o par; esta tela não.
        */
        setError(null);
      })
      .catch((e: unknown) => setError(falhaDe(e)));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const canOperate = user?.role === "admin" || user?.role === "operator";

  /*
    O 409 `SOURCE_UNSUPPORTED` É CAUSA, NÃO ERRO — e por isso ele saiu do
    `alert("Erro 409")`.

    O 409 não é falha do portal nem recusa do equipamento: é incompatibilidade
    PERMANENTE entre a ação e a fonte, e a mensagem já vem pronta do servidor
    porque é afirmação sobre a topologia de fontes — a tela pode encurtar, não
    pode inventar. "Erro 409" faz o operador abrir chamado para um equipamento
    que não tem defeito nenhum.

    A frase vai para o CARD, num `role="status"` com `aria-live`, e não para um
    `alert()`: o modal nativo tira o foco, não é lido junto do equipamento de
    que fala e some sem deixar rastro.
  */
  async function enviarComando(
    cpId: string,
    rota: string,
    corpo: Record<string, unknown>,
    marca: string,
  ) {
    setBusy(cpId + ":" + marca);
    setFalha(null);
    try {
      const res = await fetch(`/api/chargers/${encodeURIComponent(cpId)}/${rota}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corpo),
      });
      if (res.ok) {
        load();
        return;
      }
      const corpoDaFalha = (await res.json().catch(() => null)) as unknown;
      setFalha({ cpId, frase: fraseDeFalhaDeComando(res.status, corpoDaFalha) });
      /* O 409 muda a TOPOLOGIA conhecida pela tela (a fonte declarou a
         capacidade impossível), então recarrega: o botão tem de nascer
         desabilitado da próxima vez, em vez de voltar a oferecer o comando. */
      if (erroDeFonteNaoSuportada(corpoDaFalha) !== null) load();
    } finally {
      setBusy(null);
    }
  }

  async function remoteStart(cpId: string) {
    await enviarComando(cpId, "remote-start", { connectorId: 1, idTag: "CARD-DEMO-001" }, "start");
  }

  async function reset(cpId: string) {
    if (!confirm(`Reiniciar ${cpId}?`)) return;
    await enviarComando(cpId, "reset", { type: "Soft" }, "reset");
  }

  /*
    O ERRO NÃO SUBSTITUI MAIS A PÁGINA.

    Era `if (error) return <div>…` — o retorno antecipado apagava o `<h1>`, os
    filtros, a região `aria-live` e o botão de exportar. Numa tela que se
    atualiza a cada 5 s, um blip trocava a página inteira por um retângulo
    vermelho e, sem a limpeza do erro no sucesso, nunca voltava. O erro agora é
    um BANNER dentro do layout (ver `bannerDeFalha`, montado abaixo do
    cabeçalho): o resto da tela continua de pé, e a `aria-live` continua viva
    para anunciar o retorno.
  */
  const chargers = envelope?.chargers ?? null;
  /*
    O LIMIAR DE FRESCOR VEM DO SERVIDOR, e só dele.

    `configuration.staleAfterSeconds` é `OCPP_HEARTBEAT_INTERVAL × 3`, calculado
    no servidor. Hardcodar 900 aqui seria a quinta grafia do mesmo limiar nesta
    base — hoje `ocpp.ts` usa um `cutoff` próprio, esta tela decidia no cliente e
    `modules-extra` decidia de novo. Convergem em `staleAfterSeconds`.
  */
  const cfg = configuracaoDeCarregadores(envelope?.configuration.staleAfterSeconds ?? 0);
  /* O RECORTE, DECLARÁVEL: a rota corta por aprovação e por garagem, então
     `amostrados` e `totalVisivel` divergem e a tela tem de poder dizer isso. */
  const ressalvaDeRecorte =
    envelope == null
      ? null
      : ressalvaDeRecorteDeCarregadores(envelope.amostrados, envelope.totalVisivel);

  /*
    O PREDICADO DE SINAL É UM SÓ, E MORA EM `chargers-contrato.ts`.

    Havia aqui um `contatoNaoSustentaDe` local, e `modules-extra` tinha o mesmo
    predicado escrito INLINE duas vezes — três cópias divergentes dentro do
    conserto que existia para impedir cópias. **Nenhuma cobria `sem-leitura`**,
    e o subtítulo saía "19 carregadores, 1 com contato corrente, 17 sem sinal":
    1 + 17 = 18. O décimo-nono (`FZ043`, que NUNCA reportou) não estava em balde
    nenhum, com o card dele degradado duas dobras abaixo.

    A PARTIÇÃO É TOTAL por construção — `semSinalDe` e a sua negação cobrem o
    conjunto — e há teste varrendo a igualdade `semSinal + corrente === total`.

    O CLAUDE.md manda as duas coisas no padrão STALE: *"contadores agregados
    excluem os stale OU os separam em card próprio (Sem sinal: N)"*. Faço as
    duas: sai do numerador e ganha contagem própria.
  */
  // Subtítulo: nº total + contato corrente. Fica em "—" antes do primeiro fetch
  // pra não mentir "0" enquanto ainda carregando.
  const total = chargers?.length ?? null;
  const onlineNow = chargers?.filter((c) => !semSinalDe(c, cfg)).length ?? null;
  const semSinal = chargers?.filter((c) => semSinalDe(c, cfg)).length ?? null;
  /*
    A NUMERACAO SAI DA LISTA COMPLETA, e nao da filtrada.

    `visibleChargers` abaixo aplica filtro de texto e de local. Se o numero
    viesse da posicao na lista VISIVEL, filtrar renumeraria: o carregador 3
    viraria 1 sem o equipamento mudar. Por isso ela se calcula aqui, sobre
    `chargers`, e se consulta por `cpId` la embaixo. Ha teste varrendo isso.
  */

  const visibleChargers = chargers
    ?.filter((charger) => {
      const byStatus = statusFilter === "all" || deriveStationState(charger) === statusFilter;
      const byLocation = !locationFilter.trim() || (charger.location ?? "").toLocaleLowerCase("pt-BR").includes(locationFilter.trim().toLocaleLowerCase("pt-BR"));
      return byStatus && byLocation;
    })
    /*
      A ORDEM DOS CARDS SEGUE A NUMERACAO — achado na validacao ao vivo.

      Com os rotulos prontos e a lista na ordem da API, a tela mostrava
      "Carregador 3" primeiro: os numeros estavam certos e a LEITURA estava
      errada. Um numero ordinal cuja posicao na tela nao corresponde a ele
      convida o operador a achar que o numero e outra coisa.

      No ambiente real a ordem da API calha de coincidir (`id` 2, 19, 20), e e
      exatamente por isso que depender dela e fragil: coincidencia nao e
      contrato, e o `ORDER BY` do servidor nao e nosso.

      Ordena pelo NOME EXIBIDO (`nomeDoCarregador`, que é o `label` tipo
      "GP1 C12" quando identifica, senão o `cpId`), com `numeric` para que
      "GP1 C2" venha antes de "GP1 C10". A posição e o título saem da MESMA
      fonte, e deixam de poder divergir.
    */
    .sort((a, b) =>
      nomeDoCarregador(a).localeCompare(nomeDoCarregador(b), "pt-BR", {
        numeric: true,
        sensitivity: "base",
      }),
    );
  /*
    OS DOIS ZEROS, também aqui — e `valorDeParcela` estava disponível.

    Com a lista vazia o subtítulo dizia "0 carregadores, **0** online agora". O
    primeiro zero é verdade sobre a topologia; o segundo é uma PARCELA de um
    conjunto vazio, e lê-se como "nenhum dos carregadores está online" —
    afirmação sobre carregadores que não existem. Mesma família do KPI "COM
    SINAL", numa tela que esta rodada alterou.
  */
  const sub =
    total == null
      ? "Carregando lista de carregadores…"
      : total === 0
        ? "Nenhum carregador registrado"
        : `${total} ${total === 1 ? "carregador" : "carregadores"}, ${
            /* `onlineNow` é `null` antes do primeiro fetch, e `valorDeParcela`
               recusa o que não é inteiro — os dois caminhos dão travessão, que
               é o vocabulário certo para "ainda não sei". */
            (onlineNow === null ? null : valorDeParcela(onlineNow, total)) ?? "—"
          } com contato corrente${
            /* "Sem sinal: N" em contagem PRÓPRIA — padrão STALE do CLAUDE.md.
               Sem ela, tirar os vencidos do numerador de "online" esconderia a
               existência deles, que é o defeito oposto. */
            semSinal !== null && semSinal > 0 ? `, ${semSinal} sem sinal` : ""
          }`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Operação</span>
          <h1>Carregadores</h1>
          <div className="nx-page-head__sub">{sub}</div>
          {/* O RECORTE, DITO. Sem isto o subtítulo falava de 19 ao lado de uma
              topologia de 21 visíveis, sem nada que permitisse à tela declarar
              que estava vendo uma amostra. `null` quando não há recorte — uma
              ressalva permanente treina o operador a ignorá-la. */}
          {ressalvaDeRecorte !== null && (
            <p className="nx-page-head__sub" style={{ color: "var(--nx-mute)", margin: 0 }}>
              {ressalvaDeRecorte}
            </p>
          )}
          {/*
            A REGIÃO DE ANÚNCIO, SEMPRE MONTADA.

            Ela existe vazia desde o primeiro render e só MUDA DE CONTEÚDO — é a
            forma que os leitores de tela anunciam de maneira confiável. Uma
            região criada no mesmo instante em que o texto aparece é anunciada de
            forma irregular, e esta é a única notícia que a tela dá sobre o
            resultado de um comando (o `alert()` nativo, que saiu, tirava o foco
            e sumia sem deixar rastro).
          */}
          <p
            id="nx-falha-de-comando"
            className="nx-sr-only"
            role="status"
            aria-live="polite"
          >
            {falha === null ? "" : `${falha.cpId}: ${falha.frase}`}
          </p>
          {/*
            O BANNER DE FALHA DE CARGA, DENTRO DO LAYOUT e sempre montado.

            `role="alert"` para o leitor de tela anunciar quando ele aparece.
            Sempre no DOM (conteúdo condicional, não o elemento) pela mesma
            razão da região de comando: um `alert` criado no instante do erro é
            anunciado de forma irregular. A frase vem de `fraseDeFalhaDeCarga` —
            a de 403 não manda "tentar de novo", porque não resolve. Ver
            `lib/falha-de-carga.ts`.
          */}
          <p
            role="alert"
            aria-live="assertive"
            className={error === null ? "nx-sr-only" : undefined}
            style={
              error === null
                ? undefined
                : {
                    margin: "var(--nx-space-2) 0 0",
                    padding: "0.6rem 0.8rem",
                    borderRadius: "var(--nx-radius-sm)",
                    color: "var(--nx-alert-ink)",
                    background: "var(--nx-alert-bg)",
                    border: "1px solid var(--nx-alert-ink)",
                    fontSize: ".85rem",
                  }
            }
          >
            {error === null ? "" : fraseDeFalhaDeCarga(error, "os carregadores")}
          </p>
        </div>
        <div className="nx-page-head__actions">
          {/* Export só faz sentido depois que a lista foi carregada e tem
              pelo menos um item — evita gerar XLSX vazio na 1ª renderização.
              O servidor filtra `approved` conforme 17.10; aqui não passamos
              query — pega a lista canônica. */}
          <DownloadButton
            href="/api/reports/xlsx/chargers"
            filename={`nxvolt-chargers-${new Date().toISOString().slice(0, 10)}.xlsx`}
            label="Exportar XLSX"
            variant="secondary"
            disabled={chargers == null || chargers.length === 0}
            title={
              chargers == null
                ? "Aguardando carregamento da lista"
                : chargers.length === 0
                  ? "Nenhum carregador para exportar"
                  : "Baixa a lista completa em XLSX"
            }
          />
        </div>
      </div>

      {chargers != null && chargers.length > 0 && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-4)" }}>
          <div className="nx-filters">
            <label className="nx-field"><span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as "all" | ChargerState)}>
                <option value="all">Todos</option><option value="online">Disponível</option><option value="charging">Em uso</option><option value="faulted">Falha</option><option value="offline">Fora do ar</option>
              </select>
            </label>
            <label className="nx-field"><span>Local de instalação</span>
              <input value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)} placeholder="Filtrar por local" />
            </label>
          </div>
        </div>
      )}

      {chargers != null && chargers.length === 0 && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-4)" }}>
          <p style={{ color: "var(--nx-mute)", margin: 0 }}>
            Nenhum carregador registrado. Conecte um simulador ou charger real
            em
            <code className="mono"> ws://.../ocpp/&lt;cpId&gt;</code>.
          </p>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "1rem",
          marginTop: "var(--nx-space-4)",
        }}
      >
        {chargers == null &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={`skel-${i}`} className="nx-card" style={{ padding: "1rem" }}>
              <Skeleton variant="row" height={110} />
            </div>
          ))}
        {visibleChargers?.map((c) => {
          /*
            O CONTATO, pelo critério ÚNICO — e ele acrescenta um eixo que esta
            tela não tinha.

            Antes `isStale` era só `!connected`. Medido ao vivo: um carregador
            com `connected = true` e carimbo 3 H NO FUTURO saía "livre", em
            verde, sem nenhuma ressalva — estado fisicamente impossível
            apresentado como operação normal (BUG-SANITY do CLAUDE.md). A
            conectividade e a SITUABILIDADE NO TEMPO do último contato são duas
            perguntas, e agora têm duas respostas.
          */
          const contato = contatoApresentado(c, cfg, fmtAge);
          const state = deriveStationState(c);
          const physical = c.connectors.filter((cn) => cn.connectorId > 0);
          /*
            O MESMO `semSinalDe` do subtítulo — e isto é o que impede o card e o
            KPI de discordarem.

            Os `connector.status` são o ÚLTIMO snapshot em qualquer um dos
            quatro casos — não "carregando agora", e sim "estava carregando
            quando a fonte parou de responder por ele".
          */
          /* A DECISÃO vem pronta no objeto — a tela não a recompõe. */
          const isStale = contato.semSinal;
          /*
            O PILL PRINCIPAL DEGRADA JUNTO, e o rótulo NOMEIA A CAUSA.

            Sem isto o card ficava com os conectores em cinza e o pill grande em
            verde dizendo "livre" — duas afirmações incompatíveis a dois
            centímetros uma da outra.

            `rotuloDeSemSinal` é `Record` exaustivo: "nunca reportou" para quem
            nunca teve leitura, "carimbo à frente" para o que não é situável no
            tempo, "sem sinal" para o vencido, "sem conexão" para o que a fonte
            marca fora com carimbo corrente. Um "Offline" genérico aqui era o
            que fazia `FZ043` sair **"Offline"** nesta tela e **"sem sinal"** no
            Mural — a mesma máquina, dois rótulos.

            A PALETA SAI DO `tom`, e não de uma comparação de estado aqui.

            Era `contato.estado === "futura" ? "faulted" : "offline"` — uma
            SEGUNDA decisão sobre a gravidade do estado, escrita na tela, ao
            lado da que `TRATAMENTO_DA_LEITURA` já publica. `tom === 'perigo'` é
            o carimbo futuro (o dado aparenta ser o mais fresco de todos e não
            pode ser situado no tempo); os demais são aviso ou neutro.
          */
          const st = contato.paleta === null
            ? styleFor(state)
            : {
                /* A PALETA vem resolvida do contrato. Era
                   `contato.tom === "perigo" ? …` — uma segunda decisão sobre a
                   gravidade, escrita na tela, e a partição de `tom` tinha
                   `vivo` como classe unitária de `corrente`: dava para
                   reconstruir o predicado inteiro a partir dela. */
                ...styleFor(contato.paleta),
                label: maiuscula(contato.rotuloSemSinal ?? ""),
                /* Nada pulsa: sem contato situável não há operação viva a
                   representar. */
                dotAnim: false,
              };
          const motivoSemComando = motivoDeComandoIndisponivel(c);
          const legenda = isStale
            ? legendaDeConectoresDegradados(c, cfg)
            : null;
          const falhaDeste = falha?.cpId === c.cpId ? falha.frase : null;
          return (
            <article
              key={c.id}
              className="nx-card"
              style={{
                padding: "1rem",
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: "1rem",
                borderLeft: `4px solid ${st.color}`,
                background: `linear-gradient(90deg, ${st.bg} 0%, transparent 40%), var(--nx-bg)`,
              }}
            >
              <ChargerIcon accent={st.color} />

              <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
                {/* Cabeçalho: CP ID + estado principal */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    {/*
                      O NUMERO E ORDINAL; O `cpId` E A IDENTIDADE.

                      O usuario pediu "carregador 1, 2, 3" — e o numero e posicao
                      numa lista ordenada por `cpId`, nao um identificador. Um
                      quarto carregador entra na posicao alfabetica dele e pode
                      empurrar os outros. Ver `numeracaoDeCarregadores`.

                      Por isso o `cpId` CONTINUA NA TELA, na linha de apoio: e
                      ele que aparece no log, no chamado de suporte e na URL. Um
                      operador que so visse "1" nao consegue pedir ajuda.
                    */}
                    <Link
                      to={`/app/chargers/${c.cpId}`}
                      className={nomeDoCarregador(c) === c.cpId ? "mono" : undefined}
                      style={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color: "var(--nx-ink)",
                        textDecoration: "none",
                      }}
                      title={`${nomeDoCarregador(c)} — ${c.cpId}`}
                    >
                      {nomeDoCarregador(c)}
                    </Link>
                    {/* A série/cp_id (chave natural: log, URL de suporte) fica
                       ABAIXO do nome. Só quando o nome exibido NÃO é o próprio
                       cpId — carregador sem label identificável —, para o cpId
                       não aparecer duas vezes no mesmo card. */}
                    {nomeDoCarregador(c) !== c.cpId && (
                      <div
                        className="mono"
                        style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}
                        title="Série / cp_id do equipamento"
                      >
                        {c.cpId}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: ".72rem",
                        color: "var(--nx-mute)",
                        marginTop: 2,
                      }}
                    >
                      {/*
                        APELIDO DE EXIBICAO: `telluspower` sai como `Nansen`.

                        O DADO NAO MUDA — `vendor` e regravado a cada reconexao a
                        partir do `BootNotification`, entao corrigir o banco
                        duraria ate o proximo boot. Ver `apelidoDeFabricante`.

                        O CUSTO: log e suporte dizem `telluspower` e a tela diz
                        `Nansen`. O `title` carrega o valor real para quem
                        precisar cruzar com o log — e e so onde ele aparece.
                      */}
                      {/*
                        O PREFIXO "Fabricante:" NÃO É DECORAÇÃO.

                        Sem ele esta linha imprimia só `Nansen` logo abaixo do
                        `cpId`, no lugar onde o olho procura o NOME do
                        equipamento — e `Nansen` é o fabricante. Com
                        `labelIsIdentifying` falso a tela acabara de recusar
                        imprimir o rótulo como nome; sem o prefixo ela
                        reintroduzia a mesma leitura pela linha de baixo. As
                        linhas irmãs já têm rótulo ("Local:", "Série:",
                        "Fonte:"), então esta era a única sem.
                      */}
                      Fabricante:{" "}
                      {/* `fabricanteEModelo` e o UNICO lugar que aplica o
                          apelido, para as cinco telas escreverem igual. */}
                      <span title={fabricanteEModelo(c).titulo}>
                        {fabricanteEModelo(c).texto}
                      </span>
                      {c.firmware ? ` · fw ${c.firmware}` : ""}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}>
                      Local: {c.location ?? "não informado"}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}>
                      {/* AUSÊNCIA, e não impossibilidade: a fonte FORNECE o
                          campo (vem 22 em quase todos, inclusive em "DC Dual"),
                          e ele está em quarentena na sincronia — não entra em
                          `unsupportedFields`, e dizer "não fornecido" aqui seria
                          falso. "Não informada" é a frase certa. */}
                      Potência nominal: {c.nominalPowerKw == null ? "não informada" : `${c.nominalPowerKw} kW`}
                    </div>
                    <div
                      className="mono"
                      style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}
                      title={
                        c.source === "vgpl"
                          ? "Nos equipamentos sincronizados da origem este valor é sintetizado lá (NSN- + id), não é número de série de fábrica."
                          : undefined
                      }
                    >
                      Série: {c.serialNumber ?? "não informada"}
                    </div>
                    <div style={{ fontSize: ".72rem", color: "var(--nx-mute)", marginTop: 2 }}>
                      {/* A PROCEDÊNCIA NA TELA: com duas fontes visíveis, "este
                          carregador parou" e "a integração parou" são causas
                          diferentes, e o operador precisa saber de quem se fala
                          antes de abrir chamado com quem. */}
                      Fonte: {c.sourceLabel}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "2px 10px",
                      borderRadius: 999,
                      background: st.bg,
                      border: `1px solid ${st.color}`,
                      color: st.color,
                      fontSize: ".8rem",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: st.color,
                        animation: st.dotAnim ? "nx-pulse 1.4s ease-in-out infinite" : undefined,
                      }}
                      aria-hidden="true"
                    />
                    {st.label}
                  </div>
                </div>

                {/* Conectores como bolinhas rotuladas */}
                <div>
                  <div
                    style={{
                      fontSize: ".7rem",
                      textTransform: "uppercase",
                      letterSpacing: ".05em",
                      color: "var(--nx-mute)",
                      marginBottom: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    Conectores
                    {/*
                      A LEGENDA NOMEIA A CAUSA, e antes era uma frase só.

                      "último snapshot antes da desconexão" aparecia sobre um
                      carregador `connected = true` com carimbo 3 h NO FUTURO (que
                      não está desconectado) e sobre um que NUNCA reportou (para o
                      qual não existe "antes da desconexão"). Afirmar uma
                      desconexão que não aconteceu é a mesma família de defeito
                      que esta fase persegue, no texto da própria ressalva.
                    */}
                    {legenda !== null && (
                      <span
                        style={{
                          textTransform: "none",
                          fontSize: ".7rem",
                          fontWeight: 400,
                          color: "var(--nx-mute)",
                          fontStyle: "italic",
                          letterSpacing: 0,
                        }}
                        title={contato.fraseDeConexao}
                      >
                        · {legenda}
                      </span>
                    )}
                  </div>
                  {physical.length === 0 ? (
                    <span style={{ color: "var(--nx-mute)", fontSize: ".8rem" }}>
                      {/*
                        IMPOSSIBILIDADE × ESPERA, e aqui ela é dizível.

                        "Aguardando StatusNotification" promete que esperar
                        resolve. Para a fonte que declara `connector_topology`
                        impossível isso é falso: a view de origem deriva o
                        conjunto de conectores do HISTÓRICO de sessões, e um
                        conector que nunca teve sessão não existe lá — nenhum
                        StatusNotification vai chegar a este portal.
                      */}
                      {fonteForneceCapacidade(c, "connector_topology")
                        ? "Aguardando StatusNotification"
                        : `${c.sourceLabel} não publica a topologia de conectores deste equipamento.`}
                    </span>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {physical.map((cn) => {
                        // Quando o CP está offline, força o pill do conector
                        // para a paleta cinza (offline) — o valor OCPP fica
                        // como legenda menor. Assim o operador não vê laranja
                        // "Charging" pulsante num CP que sequer responde.
                        const cst = styleFor(
                          isStale ? "offline" : deriveConnectorState(cn.status),
                        );
                        return (
                          /*
                            O PREENCHIMENTO DO CHIP E DECORATIVO — medido, nao
                            argumentado.

                            O fundo solido mede 1,06–1,14:1 contra a pagina, e a
                            pergunta legitima e se isso viola 1.4.11. Nao viola,
                            e a razao esta nos DOIS portadores que existem aqui:

                            1.4.1 (cor sozinha): o estado vai como TEXTO dentro
                            do chip (`{cn.status}` — "Charging", "Available",
                            "Faulted"). A cor e reforco, nao portador unico.

                            1.4.11 (objeto grafico): o CONTORNO do chip e
                            `1px solid ${cst.color}`, a mesma tinta do rotulo.
                            Medido contra a pagina: 5,60 / 5,02 / 6,47 / 11,65:1
                            — o limite de 3:1 e para o contorno do objeto, e ele
                            passa com folga nos quatro estados.

                            Com rotulo textual E contorno acima de 3:1, o
                            preenchimento nao carrega informacao: ele e a
                            superficie sobre a qual o texto e medido (4,75–9,7:1,
                            em `contrast.test.ts`). Se um dia o rotulo sair do
                            chip, o preenchimento passa a carregar o estado e
                            volta a precisar de 3:1.
                          */
                          <span
                            /* `connectorId` e não `id`: o contrato publicado NÃO
                               traz a chave primária de `public.connectors` — e
                               foi o `tsc` que cobrou, agora que a linha vem de
                               `@nxvolt/shared` em vez da declaração local. O
                               `connectorId` é único dentro do carregador, que é
                               o escopo desta lista. */
                            key={cn.connectorId}
                            /* O MESMO RÓTULO no `title`: com `status: ""` isto
                               virava `title=""`, e um `title` vazio é um
                               atributo que existe e não informa — pior que
                               ausente, porque o leitor de tela pode anunciá-lo
                               e o navegador reserva a dica. */
                            title={
                              isStale
                                ? `Último estado reportado: ${rotuloDoStatusDoConector(
                                    cn.status,
                                  )} (carregador offline agora)`
                                : rotuloDoStatusDoConector(cn.status)
                            }
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "3px 9px",
                              borderRadius: 999,
                              background: cst.bg,
                              border: `1px solid ${cst.color}`,
                              color: cst.color,
                              fontSize: ".72rem",
                              fontWeight: 600,
                              /*
                                A RECEITA DE DEGRADAÇÃO DA FROTA, e `opacity`
                                SAIU — medido, não argumentado.

                                `opacity` cai sobre o fundo E a tinta, então
                                compõe os dois contra a superfície: o pill
                                `Charging` media **5,79:1 → 3,23:1** no claro e
                                **7,45:1 → 4,30:1** no escuro, em `.72rem`, que
                                é texto NORMAL. Reprova AA nos dois temas.

                                `ESTILO_DEGRADADO_TEXTO` é a receita que
                                `FleetCells` já usa depois de a mesma
                                composição reprovar lá uma vez:
                                `--nx-stale-value-ink` + `grayscale(.6)`, com
                                opacidade cheia.

                                DESVIO DECLARADO DO CLAUDE.md, com a troca que o
                                justifica. O padrão STALE do CLAUDE.md pede
                                `opacity: 0.55`; aqui ela é `1`, e `grayscale(.6)`
                                sobre o cinza que o pill stale já tem
                                (`--nx-state-unknown-*`) move pouco o pixel — o
                                Δ visual é quase nulo. **O PORTADOR DA
                                DEGRADAÇÃO, então, deixou de ser a cor e passou a
                                ser o TEXTO `(último)`** renderizado abaixo, que é
                                não-cromático e sobrevive a daltonismo e a
                                impressão P&B (1.4.1). A troca é deliberada:
                                perde-se o sinal cromático forte que reprovava AA
                                e ganha-se um portador textual que não depende de
                                contraste de cor. Remover o `(último)` reabre o
                                buraco — há teste de sanity exigindo `selo ===
                                (último)`.
                              */
                              ...(isStale ? ESTILO_DEGRADADO_TEXTO : {}),
                            }}
                          >
                            {/*
                              `N.M` — conector 2 do carregador 1 e "1.2".

                              O `connectorId = 0` e o PONTO DE RECARGA na OCPP
                              1.6, nao um conector fisico: "1.0" seria um bocal
                              que nao existe. O filtro `connectorId > 0` acima
                              continua valendo, e `rotuloDoConector` devolve
                              `null` para o zero como segunda barreira.

                              Sem numeracao (lista vazia, `cpId` desconhecido) o
                              rotulo cai para `#M`, que e verdadeiro ainda que
                              menos legivel.
                            */}
                            <strong>
                              {`#${cn.connectorId}`}
                            </strong>
                            <span
                              style={{
                                display: "inline-block",
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                background: cst.color,
                                // Nada de pulsar quando offline: sem sinal do CP,
                                // não há operação viva pra representar.
                                animation:
                                  cst.dotAnim && !isStale
                                    ? "nx-pulse 1.4s ease-in-out infinite"
                                    : undefined,
                              }}
                              aria-hidden="true"
                            />
                            {/*
                              O ROTULO NUNCA VAI VAZIO — e isto sustenta o
                              argumento de 1.4.11 escrito logo acima.

                              A coluna tem `default('Unavailable')`, mas um CP
                              malformado mandando `status: ""` renderizava o chip
                              SEM palavra de estado. Sem rotulo, a cor vira
                              portador UNICO: 1.4.1 passa a ser violado e o
                              contorno deixa de bastar, porque nao ha mais texto
                              para a cor reforcar.

                              A premissa do argumento e "existe rotulo textual" —
                              entao ela tem de ser GARANTIDA, nao suposta.
                            */}
                            {rotuloDoStatusDoConector(cn.status)}
                            {isStale && (
                              <em
                                style={{
                                  fontStyle: "normal",
                                  fontSize: ".65rem",
                                  fontWeight: 400,
                                  color: "var(--nx-mute)",
                                }}
                              >
                                (último)
                              </em>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/*
                  Rodapé: CONTATO + ações.

                  Era `Último heartbeat: {fmt(c.lastHeartbeatAt)}` — campo que o
                  servidor removeu do contrato. Medido ao vivo antes do
                  conserto: os 19 cards imprimiam "Último heartbeat: —", porque
                  `undefined` atravessava o formatador e saía travessão. E a
                  frase estava errada mesmo quando o campo existia: em 16 dos 19
                  o carimbo é o POLL da integração de origem, não um heartbeat
                  de equipamento. `contact.means` é o que decide a palavra.
                */}
                <div
                  style={{
                    display: "grid",
                    gap: 6,
                    borderTop: "1px solid var(--nx-line)",
                    paddingTop: 8,
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: ".72rem",
                        color: "var(--nx-mute)",
                        /* Padrão STALE: degrada o VALOR, não o apaga. */
                        filter: contato.degradado ? "grayscale(0.6)" : undefined,
                      }}
                      title={contato.fraseDeConexao}
                    >
                      <strong style={{ fontWeight: 600 }}>{contato.rotulo}:</strong>{" "}
                      {idadeCurtaDoContato(c.contact, fmtAge)}
                    </span>
                    {canOperate && motivoSemComando === null && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          type="button"
                          className="nx-btn nx-btn--sm"
                          onClick={() => void remoteStart(c.cpId)}
                          disabled={!c.connected || busy === c.cpId + ":start"}
                          title="Enviar RemoteStartTransaction"
                        >
                          Start
                        </button>
                        <button
                          type="button"
                          className="nx-btn nx-btn--sm nx-btn--ghost-dark"
                          onClick={() => void reset(c.cpId)}
                          disabled={!c.connected || busy === c.cpId + ":reset"}
                          title="Enviar Reset OCPP"
                        >
                          Reset
                        </button>
                      </div>
                    )}
                  </div>
                  {/*
                    Os botões de comando SOMEM quando a capacidade não existe
                    (`motivoSemComando !== null`): um controle que nunca poderá
                    ser acionado é ruído na ordem de tabulação, não um botão.

                    A caixa de texto que explicava a indisponibilidade foi
                    removida a pedido do cliente (vitrine): expunha a arquitetura
                    de origem do dado. A ausência dos botões já comunica que não
                    há comando a dar aqui.
                  */}
                  {/*
                    A RESSALVA DE CARIMBO COMPARTILHADO — e ela vale nos DOIS
                    sentidos. 8 carregadores com o MESMO carimbo de 42 s não são
                    8 sinais de vida, e 16 parados no MESMO SEGUNDO são uma ponte
                    caída e não 16 equipamentos mortos.
                  */}
                  {contato.fraseDeCompartilhamento !== null && (
                    <span style={{ fontSize: ".7rem", color: "var(--nx-state-stale-ink)" }}>
                      {contato.fraseDeCompartilhamento}
                    </span>
                  )}
                  {/* A CAUSA da última falha de comando, junto do equipamento de
                      que ela fala. Ver `enviarComando`. */}
                  {/* O ANÚNCIO não acontece AQUI — ver a região de página em
                      `nx-falha-de-comando`. Uma região `aria-live` por card
                      seriam 19 regiões vazias competindo; e uma região criada
                      junto com o texto é anunciada de forma irregular pelos
                      leitores de tela. Aqui fica só o texto VISÍVEL, junto do
                      equipamento de que ele fala. */}
                  {falhaDeste !== null && (
                    <p
                      style={{
                        fontSize: ".72rem",
                        color: "var(--nx-alert-ink)",
                        background: "var(--nx-alert-bg)",
                        border: "1px solid var(--nx-alert-ink)",
                        borderRadius: 6,
                        padding: "6px 8px",
                        margin: 0,
                      }}
                    >
                      {falhaDeste}
                    </p>
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {/*
          A CONTRADIÇÃO DE DOIS ESTADOS VAZIOS AO MESMO TEMPO.

          Com a lista VAZIA, a tela mostrava "Nenhum carregador registrado" e
          "Nenhum carregador atende aos filtros" JUNTOS — e a segunda afirma que
          existem carregadores (só que nenhum passou no filtro). Duas frases a
          dois centímetros uma da outra dizendo coisas incompatíveis, que é o
          defeito que a coluna de SOC do CCO já tinha corrigido nesta fase.

          "Nenhum atende aos filtros" só faz sentido quando HÁ carregadores.
        */}
        {visibleChargers?.length === 0 && (chargers?.length ?? 0) > 0 && (
          <p style={{ color: "var(--nx-mute)", gridColumn: "1 / -1", textAlign: "center" }}>Nenhum carregador atende aos filtros.</p>
        )}
        {/* Keyframes inline pra não precisar mexer em global.css. */}
        <style>{`
          @keyframes nx-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: .35; transform: scale(1.6); }
          }
        `}</style>
      </div>
    </div>
  );
}

// Reuso interno da callback de stop (poderia ser exportado)
export type { Charger, Connector };
